

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge, Container, Row, Col } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../Common/CompanyData/DefaultData';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './BookService.css';

const BookService = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [salons, setSalons] = useState([]);
    const [allTimings, setAllTimings] = useState([]); // New state for timing data
    
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSalon, setSelectedSalon] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(''); 
    const [bookingNotes, setBookingNotes] = useState('');
    const [bookingLoading, setBookingLoading] = useState(false);
    
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    
    const [error, setError] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [bookingConfirmation, setBookingConfirmation] = useState(null);

    const THEME_COLOR = '#065f46';
    const currency = DefaultData.DEFAULTCURRENCY || 'NGN';

    // Helper: Format 24h to 12h AM/PM
    const formatTime12h = (timeStr) => {
        if (!timeStr) return '---';
        try {
            let [hours, minutes] = timeStr.split(':');
            let h = parseInt(hours, 10);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            return `${h.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        } catch (e) { return timeStr; }
    };

    useEffect(() => {
        const salonIdFromUrl = searchParams.get('salonId');
        if (salonIdFromUrl) setSelectedSalon(salonIdFromUrl);
        checkAuthStatus();
        fetchServices();
        fetchCategories();
        fetchSalons();
        fetchTimings(); // New fetch call
    }, [searchParams]);

    useEffect(() => {
        filterServices();
    }, [selectedCategory, selectedSalon, searchTerm, services]);

    const checkAuthStatus = () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
            setIsLoggedIn(true);
            const parsedUser = JSON.parse(user);
            setCurrentUser(parsedUser);
            setCustomerName(parsedUser.name || '');
            setCustomerEmail(parsedUser.email || '');
        } else {
            setIsLoggedIn(false);
            setCurrentUser(null);
        }
    };

    const fetchServices = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETSERVICETYPE);
            if (res.data && res.data.data) {
                setServices(res.data.data.filter(s => s.isActive));
            }
        } catch (error) { console.error("Error fetching services", error); }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLCATEGORIES);
            setCategories(res.data || []);
        } catch (error) { console.error("Error fetching categories", error); }
    };

    const fetchSalons = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + "Salon/GetAll");
            setSalons(res.data || []);
        } catch (error) { console.error("Error fetching salons", error); }
    };

    const fetchTimings = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLTIMINGS);
            setAllTimings(res.data || []);
        } catch (error) { console.error("Error fetching timings", error); }
    };

    const filterServices = () => {
        let filtered = [...services];
        if (selectedCategory) filtered = filtered.filter(s => s.category === selectedCategory);
        if (selectedSalon) {
            filtered = filtered.filter(s => String(s.salonId).toLowerCase() === String(selectedSalon).toLowerCase());
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(s => 
                s.name.toLowerCase().includes(term) || s.category.toLowerCase().includes(term)
            );
        }
        setFilteredServices(filtered);
    };

    const handleBookClick = async (service) => {
        setSelectedService(service);
        setError('');
        setShowConfirmation(false);
        setBookingConfirmation(null);
        setSelectedTimeSlot('');
        if (!isLoggedIn) {
            setCustomerName(''); setCustomerEmail(''); setCustomerPhone('');
        }
        await fetchAvailableTimeSlots(service.salonId, new Date());
        setShowBookingModal(true);
    };

    // Updated: Generate slots based on Salon Open/Close Times
    const fetchAvailableTimeSlots = async (salonId, date) => {
        const dayOfWeek = date.getDay();
        const timing = allTimings.find(t => t.salonId === salonId && t.dayOfWeek === dayOfWeek);

        if (!timing || timing.isClosed) {
            setAvailableSlots([]);
            return;
        }

        const slots = [];
        let [startH, startM] = timing.openTime.split(':').map(Number);
        let [endH, endM] = timing.closeTime.split(':').map(Number);

        let current = new Date();
        current.setHours(startH, startM, 0, 0);

        const endTime = new Date();
        endTime.setHours(endH, endM, 0, 0);

        // Generate 30-minute slots from open to close
        while (current < endTime) {
            const h24 = String(current.getHours()).padStart(2, '0');
            const m = String(current.getMinutes()).padStart(2, '0');
            const time24 = `${h24}:${m}`;

            const period = current.getHours() >= 12 ? 'PM' : 'AM';
            const h12 = current.getHours() % 12 || 12;
            const time12 = `${h12.toString().padStart(2, '0')}:${m} ${period}`;

            // Make all slots available
            slots.push({ value: time24, label: time12, available: true });
            current.setMinutes(current.getMinutes() + 30);
        }
        setAvailableSlots(slots);
    };

    const handleDateChange = async (date) => {
        setSelectedDate(date);
        setSelectedTimeSlot('');
        if (selectedService) await fetchAvailableTimeSlots(selectedService.salonId, date);
    };

    const formatToNaiveISO = (date, timeSlot) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T${timeSlot}:00`;
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (!isLoggedIn && (!customerName.trim() || !customerEmail.trim())) {
            setError('Please fill required details'); return;
        }
        if (!selectedTimeSlot) { setError('Please select a time slot'); return; }

        setBookingLoading(true);
        try {
            const startStr = formatToNaiveISO(selectedDate, selectedTimeSlot);
            const [h, m] = selectedTimeSlot.split(':').map(Number);
            const dateCopy = new Date(selectedDate);
            dateCopy.setHours(h, m, 0, 0);
            const endDateObj = new Date(dateCopy.getTime() + (selectedService.durationMinutes || 60) * 60000);
            const endStr = formatToNaiveISO(endDateObj, `${String(endDateObj.getHours()).padStart(2, '0')}:${String(endDateObj.getMinutes()).padStart(2, '0')}`);

            const bookingData = {
                salonId: selectedService.salonId,
                serviceTypeId: selectedService.serviceTypeId,
                startTime: startStr,
                endTime: endStr,
                status: "PENDING",
                email: isLoggedIn ? currentUser.email : customerEmail,
                customerName: isLoggedIn ? currentUser.name : customerName,
                notes: bookingNotes,
                ...(customerPhone && { phoneNumber: customerPhone })
            };

            const response = await axios.post(`${config.APIACTIVEURL}${config.CREATEAPPPOINTMENT}`, bookingData);
            if (response.data.success) {
                const appointmentData = response.data.data;
                const displayTime = availableSlots.find(s => s.value === selectedTimeSlot)?.label || selectedTimeSlot;
                setBookingConfirmation({
                    appointmentNo: appointmentData?.appointmentNo || 'N/A',
                    serviceName: selectedService.name,
                    date: selectedDate.toLocaleDateString(),
                    time: displayTime,
                    message: response.data.message || 'Booked successfully!'
                });
                setShowConfirmation(true);
                resetBookingForm();
            } else { setError(response.data.message); }
        } catch (error) { setError('Booking failed. Try again.'); } finally { setBookingLoading(false); }
    };

    const resetBookingForm = () => {
        setSelectedTimeSlot(''); setBookingNotes(''); setSelectedDate(new Date());
        setCustomerName(''); setCustomerEmail(''); setCustomerPhone(''); setError('');
    };

    const formatPrice = (price) => `${currency} ${price?.toFixed(2) || '0.00'}`;

    return (
        <div className="page-content booking-page-wrapper">
            <Container fluid className="py-3 py-md-4">
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-header bg-white border-0 py-3">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <Button variant="light" size="sm" onClick={() => navigate(-1)} className="rounded-pill px-3 border">
                                <i className="ri-arrow-left-line"></i> Back
                            </Button>
                            <h4 className="fw-bold mb-0 text-dark">Book Service</h4>
                        </div>
                        <Row className="g-2 g-md-3 filter-section">
                            <Col xs={12} md={4}>
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0"><i className="ri-search-line text-muted"></i></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Search services..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                </div>
                            </Col>
                            <Col xs={6} md={4}>
                                <select className="form-select custom-select-style" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                                    <option value="">All Categories</option>
                                    {categories.map(c => <option key={c.categoryId} value={c.name}>{c.name}</option>)}
                                </select>
                            </Col>
                            <Col xs={6} md={4}>
                                <select className="form-select custom-select-style" value={selectedSalon} onChange={(e) => setSelectedSalon(e.target.value)}>
                                    <option value="">All Salons</option>
                                    {salons.map(s => <option key={s.salonId} value={s.salonId}>{s.name}</option>)}
                                </select>
                            </Col>
                        </Row>
                    </div>

                    <div className="card-body">
                        <Row className="g-3 g-md-4">
                            {filteredServices.length === 0 ? (
                                <Col xs={12} className="text-center py-5 text-muted">No services found match your filters.</Col>
                            ) : (
                                filteredServices.map(service => {
                                    // Get today's timing for this specific salon
                                    const todayDayId = new Date().getDay();
                                    const todayTiming = allTimings.find(t => t.salonId === service.salonId && t.dayOfWeek === todayDayId);
                                    
                                    return (
                                        <Col key={service.serviceTypeId} xs={12} md={6} lg={4}>
                                            <div className="card service-card h-100 border rounded-3 p-3 shadow-sm">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <h5 className="fw-bold text-dark mb-0 fs-6 fs-md-5">{service.name}</h5>
                                                    <span className="fw-bold text-success small">{formatPrice(service.price)}</span>
                                                </div>
                                                <Badge bg="light" text="dark" className="border mb-3 align-self-start">{service.category}</Badge>
                                                
                                                {/* Today's Timing Display */}
                                                <div className="mb-2 p-2 bg-light rounded-2 border-start border-3 border-success">
                                                    <span className="fw-bold text-muted extra-small d-block text-uppercase mb-1" style={{fontSize: '0.6rem'}}>Today's Hours</span>
                                                    {todayTiming ? (
                                                        todayTiming.isClosed ? (
                                                            <span className="text-danger small fw-bold">Closed</span>
                                                        ) : (
                                                            <span className="text-dark small fw-bold">
                                                                {formatTime12h(todayTiming.openTime)} - {formatTime12h(todayTiming.closeTime)}
                                                            </span>
                                                        )
                                                    ) : <span className="text-muted extra-small">Not set</span>}
                                                </div>

                                                <div className="mb-3 text-muted small">
                                                    <div className="mb-1"><i className="ri-store-2-line me-1"></i> {service.salon?.name || 'Salon'}</div>
                                                    <div><i className="ri-time-line me-1"></i> {service.durationMinutes} mins</div>
                                                </div>
                                                <button className="btn w-100 mt-auto fw-bold text-white btn-sm btn-md-md" style={{ backgroundColor: THEME_COLOR }} onClick={() => handleBookClick(service)}>
                                                    Book Now <i className="ri-calendar-line ms-1"></i>
                                                </button>
                                            </div>
                                        </Col>
                                    );
                                })
                            )}
                        </Row>
                    </div>
                </div>

                <Modal show={showBookingModal} onHide={() => { setShowBookingModal(false); setShowConfirmation(false); }} size="lg" centered backdrop="static" className="booking-modal-mobile">
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold fs-5" style={{ color: THEME_COLOR }}>
                            <i className="ri-calendar-check-line me-2"></i>
                            {showConfirmation ? 'Booking Success' : 'Book Appointment'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-0">
                        {showConfirmation && bookingConfirmation ? (
                            <div className="text-center py-3">
                                <div className="mb-3 text-success"><i className="ri-checkbox-circle-fill fs-1"></i></div>
                                <h5 className="fw-bold mb-3">{bookingConfirmation.message}</h5>
                                <div className="bg-light p-3 rounded-3 text-start small mb-4">
                                    <p className="mb-1"><strong>ID:</strong> {bookingConfirmation.appointmentNo}</p>
                                    <p className="mb-1"><strong>Service:</strong> {bookingConfirmation.serviceName}</p>
                                    <p className="mb-0"><strong>Time:</strong> {bookingConfirmation.date} @ {bookingConfirmation.time}</p>
                                </div>
                                <Button variant="danger" className="w-100 rounded-pill" onClick={() => setShowBookingModal(false)}>Close</Button>
                            </div>
                        ) : selectedService && (
                            <div className="booking-form-wrapper">
                                {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                                <div className="bg-light p-3 rounded-3 mb-3 border d-flex justify-content-between">
                                    <span className="fw-bold small">{selectedService.name}</span>
                                    <span className="text-success fw-bold small">{formatPrice(selectedService.price)}</span>
                                </div>

                                <form onSubmit={handleBookingSubmit}>
                                    {!isLoggedIn && (
                                        <Row className="g-2 mb-3">
                                            <Col xs={12}><Form.Control size="sm" placeholder="Name *" value={customerName} onChange={e => setCustomerName(e.target.value)} required /></Col>
                                            <Col xs={12} md={6}><Form.Control size="sm" type="email" placeholder="Email *" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} required /></Col>
                                            <Col xs={12} md={6}><Form.Control size="sm" placeholder="Phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} /></Col>
                                        </Row>
                                    )}
                                    <Row>
                                        <Col xs={12} lg={6} className="mb-3">
                                            <h6 className="fw-bold small mb-2">Select Date</h6>
                                            <div className="calendar-responsive-wrapper">
                                                <Calendar onChange={handleDateChange} value={selectedDate} minDate={new Date()} className="w-100 border rounded-3" />
                                            </div>
                                        </Col>
                                        <Col xs={12} lg={6} className="mb-3">
                                            <h6 className="fw-bold small mb-2">Available Times</h6>
                                            <div className="time-slots-grid">
                                                {availableSlots.length > 0 ? availableSlots.map((slot, i) => (
                                                    <button key={i} type="button" 
                                                        className={`time-slot-item ${selectedTimeSlot === slot.value ? 'active' : ''}`}
                                                        onClick={() => setSelectedTimeSlot(slot.value)}>
                                                        {slot.label}
                                                    </button>
                                                )) : (
                                                    <div className="col-12"><Alert variant="warning" className="small py-2">Salon is closed on this day.</Alert></div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Form.Control as="textarea" rows={2} className="mb-3 small" placeholder="Notes..." value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} />
                                    <div className="d-flex gap-2">
                                        <Button variant="light" className="flex-grow-1 small" onClick={() => setShowBookingModal(false)}>Cancel</Button>
                                        <Button type="submit" style={{ backgroundColor: THEME_COLOR }} className="flex-grow-1 text-white small" disabled={!selectedTimeSlot || bookingLoading}>
                                            {bookingLoading ? '...' : 'Confirm'}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
            </Container>
        </div>
    );
};

export default BookService;